/**
 * Prezzo degli upsell dopo lo sconto della promo.
 *
 * Esiste per una ragione precisa: il totale che si paga e la riga che il socio legge
 * nel riepilogo erano calcolati in due punti diversi del componente, con formule che
 * non coincidevano. Iscrizione e Tesseramento entravano nel totale scontati ma il
 * riepilogo mostrava il prezzo pieno; il Precision Training entrava nel totale e non
 * compariva affatto. Una sola funzione per entrambi gli usi rende impossibile che le
 * due cifre divergano di nuovo.
 *
 * I nomi dei campi sono quelli di `PromoCode.UpsellsDiscount` lato server, che il
 * `PromoCodePublicDTO` inoltra senza rinominare nulla: sono quindi l'unica forma in cui
 * questi flag arrivano davvero al client.
 */

/**
 * Nomi dei campi per ciascun upsell, verificati su `PromoCode.UpsellsDiscount`.
 *
 * ⚠️ Non aggiungere qui varianti "difensive" di questi nomi. Il componente ne
 * consultava parecchie — `membershipIsPercentage`, `applyToTarget`, `targetDiscount`,
 * `targetPercentage`, `stopngoPercentage` — e **nessuna esiste sul server**: erano
 * sempre `undefined`, quindi non cambiavano il risultato ma facevano sembrare che il
 * formato del dato fosse incerto. Se un nome cambia sul server, va cambiato qui: una
 * riga sola, e il controllo in `scripts/verify-upsell-overview.mjs` lo mostra.
 */
export const CAMPI_UPSELL = {
    registration:     { apply: 'applyToRegistration',     sconto: 'registrationDiscount',     percentuale: 'registrationPercentage' },
    membership:       { apply: 'applyToMembership',        sconto: 'membershipDiscount',       percentuale: 'membershipPercentage' },
    bia:              { apply: 'applyToBIA',               sconto: 'biaDiscount',              percentuale: 'biaPercentage' },
    targetTraining:   { apply: 'applyToTargetTraining',    sconto: 'targetTrainingDiscount',   percentuale: 'targetTrainingPercentage' },
    personalTraining: { apply: 'applyToPersonalTraining',  sconto: 'personalTrainingDiscount', percentuale: 'personalTrainingPercentage' },
    stopAndGo:        { apply: 'applyToStopAndGo',         sconto: 'stopAndGoDiscount',        percentuale: 'stopAndGoPercentage' },
    nutrition:        { apply: 'applyToNutrition',         sconto: 'nutritionDiscount',        percentuale: 'nutritionPercentage' },
};

/**
 * Upsell per cui il server **non** applica alcuno sconto, quindi nemmeno il riepilogo
 * deve mostrarne uno: `UpsellsDiscount` non ha campi per il Precision Training e il
 * ramo PRECISION_TRAINING di OrderService lo addebita pieno. Scontarlo qui mostrerebbe
 * meno di quanto viene incassato.
 */
export const UPSELL_SENZA_SCONTO = ['precisionTraining'];

/**
 * Sconto su un importo. Stessa aritmetica di `computeDiscounted` nel componente,
 * compreso il taglio a zero: uno sconto fisso più grande dell'importo non produce un
 * credito.
 */
export function scontaImporto(base, valoreSconto, inPercentuale) {
    const importo = Number(base || 0);
    const valore = Number(valoreSconto || 0);
    if (!(importo > 0) || !(valore > 0)) return { dopo: importo > 0 ? importo : 0, sconto: 0 };
    const sconto = inPercentuale ? (importo * valore / 100) : valore;
    return { dopo: Math.max(0, importo - sconto), sconto: Math.min(importo, sconto) };
}

/**
 * Prezzo di un upsell dopo la promo. Ritorna `base` invariato quando la promo non
 * tocca quell'upsell, così il chiamante non deve distinguere i due casi.
 *
 * Un upsell reso gratuito torna **0**, non `null`: nel riepilogo deve restare una voce
 * leggibile a 0 €, perché «non la vedo» e «non la pago» sono due informazioni diverse
 * per chi sta comprando.
 */
export function prezzoUpsellScontato(base, upsellsDiscount, chiave) {
    const importo = Number(base || 0);
    if (!(importo > 0)) return 0;
    if (UPSELL_SENZA_SCONTO.includes(chiave)) return importo;

    const campi = CAMPI_UPSELL[chiave];
    if (!campi || !upsellsDiscount) return importo;
    if (upsellsDiscount[campi.apply] !== true) return importo;

    return scontaImporto(
        importo,
        upsellsDiscount[campi.sconto],
        upsellsDiscount[campi.percentuale] === true,
    ).dopo;
}

/**
 * Il codice azzera assicurazione e tesseramento: sconto generale percentuale >= 100 **e** blocco sconti sugli
 * upsell presente **oppure** interruttore `coversInsuranceAndMembership` acceso (devops#410, #427).
 *
 * È la sola copia della regola nel client e rispecchia `CodiceAlCento.copreAssicurazioneETesseramento` del server:
 * se divergono, la pagina mostra e somma importi che il server poi azzera (o il contrario). L'interruttore vale
 * solo se è `true` — assente o `false` si comporta come prima — e su un codice non al 100% non cambia nulla.
 */
export function codiceAlCentoCopreUpsell(promo) {
    if (!promo) return false;
    const alCento = promo.percentage === true && Number(promo.discountAmount || 0) >= 100;
    if (!alCento) return false;
    return !!promo.upsellsDiscount || promo.coversInsuranceAndMembership === true;
}

/**
 * `upsellsDiscount` come lo applica il server: con il codice che copre le due voci assicurazione e tesseramento
 * sono azzerati anche se i loro interruttori sono spenti, e anche se il codice non ha alcun blocco upsell (basta
 * l'interruttore). Tutto il componente deve leggere questo, non il campo grezzo.
 */
export function upsellsDiscountEffettivo(promo) {
    const u = promo?.upsellsDiscount ?? null;
    if (!codiceAlCentoCopreUpsell(promo)) return u;
    return {
        ...(u || {}),
        applyToRegistration: true, registrationDiscount: 100, registrationPercentage: true,
        applyToMembership: true, membershipDiscount: 100, membershipPercentage: true,
    };
}

/**
 * Assicurazione comprata da sola (prodotto `REGISTRATION` nel checkout): l'importo del prodotto principale segue
 * la regola che il server applica nel ramo REGISTRATION di `OrderService` (codice al 100% che copre assicurazione e
 * tesseramento, oppure `registrationDiscount` se `applyToRegistration`), cioe' la stessa degli upsell.
 *
 * Il riepilogo la saltava del tutto («l'assicurazione non ha sconti da codice»): il codice veniva accettato ma il
 * totale restava a 20 €, mentre il server calcolava 0 (devops#432). Ritorna `{ dopo, sconto }`.
 */
export function prezzoAssicurazioneAcquistata(base, promo) {
    const importo = Number(base || 0);
    const dopo = prezzoUpsellScontato(importo, upsellsDiscountEffettivo(promo), 'registration');
    return { dopo, sconto: Math.max(0, importo - dopo) };
}
